// SECENEKLER — katalog urununun listede nasil satilacagi TEK yerde:
//   1) Fiyat ekseni: hangi eksen fiyati belirliyor, Etsy adi ve her degerin
//      Etsy etiketi ("1 Halka-1 Birthstone" -> "1 Circle + 1 Stone", AI ile)
//   2) Secenek setleri (kutuphane): varyasyon / kisisellestirme, urune ozel degerler
//   3) Isim yazilan urun: kac isim, ornekler, yazi tipi (Studio fotografa yazar,
//      listeye isim sorusu eklenir)
// Otomatik algilama eklentiyle ayni kurali kullanir (@erc/shared). Eksen ->
// set eslesmeleri "ad + ilk deger" kuraliyla hatirlanir (hesap ayari).

import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Sparkles, Wand2 } from "lucide-react";
import {
  suggestOptionRefs, productPriceVaries, findOptionRule, optionRuleKey,
  type ProductOptionRef, type ProductCategory, type OptionMapRule,
} from "@erc/shared";
import { authHeaders, getBackendUrl } from "../../lib/http";
import { productJewelryType } from "../../lib/jewelryType";

interface OptionSetRow {
  id: string; name: string; etsyName?: string | null; kind: string;
  values: Array<{ label: string; value?: string }>; defaultRole: "variation" | "personalization";
  productTypeKey?: string | null; chartImageUrl?: string | null;
  /** false: urune ozel set — kutuphane listelerinde cikmaz */
  inLibrary?: boolean;
}

type Perso = { enabled: boolean; slots: number; samples: string[]; font?: string; note?: string };
type CatX = ProductCategory & {
  axisRoles?: Record<string, string>;
  axisTitles?: Record<string, string>;
  axisValueLabels?: Record<string, Record<string, string>>;
  personalization?: Perso;
};

export function useOptionSets() {
  return useQuery({
    queryKey: ["option-sets"],
    staleTime: 60_000,
    queryFn: async (): Promise<OptionSetRow[]> => {
      const r = await fetch(`${getBackendUrl()}/api/option-sets`, { headers: authHeaders() });
      return r.ok ? ((await r.json()).data ?? []) as OptionSetRow[] : [];
    },
  });
}

/** Eklentide/panelde kurulan "Rexven ekseni -> set" eslesmeleri (hesap ayari) */
function useOptionRules() {
  return useQuery({
    queryKey: ["settings", "rexven_option_map"],
    staleTime: 60_000,
    queryFn: async (): Promise<OptionMapRule[]> => {
      const r = await fetch(`${getBackendUrl()}/api/settings`, { headers: authHeaders() });
      if (!r.ok) return [];
      try { const v = JSON.parse((await r.json()).data?.rexven_option_map ?? "[]"); return Array.isArray(v) ? v : []; } catch { return []; }
    },
  });
}

const abs = (u?: string | null) => (!u ? "" : /^(https?:|data:)/.test(u) ? u : `${getBackendUrl()}${u}`);
async function call<T>(path: string, method: string, body?: unknown): Promise<T> {
  const r = await fetch(`${getBackendUrl()}${path}`, { method, headers: authHeaders(), body: body === undefined ? undefined : JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `İstek başarısız (${r.status})`);
  return j.data as T;
}

export function ProductOptionsEditor({ catalogId, cat: catIn, startOpen, onOpen }: {
  catalogId: string; cat: ProductCategory; startOpen?: boolean;
  /** Verilirse ozet tiklaninca bu cagrilir (katalog karti -> urun detayi) */
  onOpen?: () => void;
}) {
  const cat = catIn as CatX;
  const qc = useQueryClient();
  const { data: sets = [], isLoading } = useOptionSets();
  const { data: rules = [] } = useOptionRules();
  const typeKey = productJewelryType(cat as unknown as { productTypeKey?: string; name?: string });
  const stored = cat.optionRefs;
  const [open, setOpen] = useState(!!startOpen);
  const [refs, setRefs] = useState<ProductOptionRef[]>(stored ?? []);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [reasons, setReasons] = useState<Record<string, string>>({});
  // Oneriler yalnizca kutuphane setlerinden (+ hatirlanan eslesmeler)
  const suggestion = useMemo(() => suggestOptionRefs(cat as never,
    sets.filter((s) => s.inLibrary !== false || rules.some((r) => r.optionSetId === s.id)) as never, typeKey, rules), [cat, sets, typeKey, rules]);
  const nameOf = (id: string) => sets.find((s) => s.id === id)?.name ?? "?";

  // ── 1) Fiyat ekseni ──
  const multiAxes = (cat.source?.variationTypes ?? []).filter((a) => (a.values?.length ?? 0) > 1);
  const storedPrice = Object.entries(cat.axisRoles ?? {}).find(([, r]) => r === "price")?.[0] ?? "";
  const priceReal = productPriceVaries(cat as never);
  const [priceAxis, setPriceAxis] = useState<string>(storedPrice && (priceReal || multiAxes.length > 0) ? storedPrice : "");
  const axisValues = multiAxes.find((a) => a.name === priceAxis)?.values ?? [];
  const [etsyName, setEtsyName] = useState(cat.axisTitles?.[storedPrice] ?? "");
  const [labels, setLabels] = useState<Record<string, string>>(cat.axisValueLabels?.[storedPrice] ?? {});
  const [saveToLibrary, setSaveToLibrary] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);

  async function aiLabels() {
    if (!priceAxis || axisValues.length === 0) return;
    setAiBusy(true); setMsg(null);
    try {
      const r = await call<{ etsyName: string; labels: Record<string, string> }>("/api/option-sets/ai-etsy-labels", "POST",
        { axisName: priceAxis, values: axisValues, productTitle: cat.name });
      if (r.etsyName) setEtsyName(r.etsyName);
      setLabels((l) => ({ ...l, ...r.labels }));
    } catch (e) { setMsg(e instanceof Error ? e.message : "AI çevirisi başarısız"); }
    finally { setAiBusy(false); }
  }

  // ── 3) Isim yazilan urun ──
  const p0 = cat.personalization;
  const [persoOn, setPersoOn] = useState(!!p0?.enabled);
  const [slots, setSlots] = useState(p0?.slots ?? 1);
  const [samples, setSamples] = useState<string[]>(p0?.samples ?? []);
  const [font, setFont] = useState(p0?.font ?? "");
  const [note, setNote] = useState(p0?.note ?? "");

  const [creatingSet, setCreatingSet] = useState<string | null>(null);
  /** Kutuphanede olmayan ekseni yeni set olarak kaydet ve urune ekle */
  async function createSetFor(u: { name: string; values: string[]; role: ProductOptionRef["role"] }) {
    setCreatingSet(u.name); setMsg(null);
    try {
      const set = await call<OptionSetRow>("/api/option-sets", "POST",
        { name: u.name, kind: "list", values: u.values.map((label) => ({ label })), defaultRole: u.role, productTypeKey: typeKey ?? undefined, inLibrary: false });
      await qc.invalidateQueries({ queryKey: ["option-sets"] });
      setRefs((rs) => [...rs, { optionSetId: set.id, role: u.role }]);
      setReasons((x) => ({ ...x, [set.id]: "yeni set (üründen)" }));
    } catch (e) { setMsg(e instanceof Error ? e.message : "Set oluşturulamadı"); }
    finally { setCreatingSet(null); }
  }

  /** Hepsini kaydet: fiyat ekseni, setler, isim alani */
  async function saveAll(clearRefs = false) {
    setBusy(true); setMsg(null);
    try {
      const base = `/api/supplier-catalogs/${catalogId}/categories/${cat.id}`;
      // Fiyat ekseni + etiketler
      const cleanLabels = Object.fromEntries(Object.entries(labels).filter(([v, l]) => axisValues.includes(v) && l.trim()).map(([v, l]) => [v, l.trim()]));
      await call(`${base}/price-axis`, "PATCH", { axisName: priceAxis || null, etsyName: etsyName.trim(), labels: cleanLabels });
      // Istenirse fiyat ekseni kutuphaneye (etiket = Etsy, deger = Rexven) + hatirla
      if (priceAxis && saveToLibrary && Object.keys(cleanLabels).length) {
        const values = axisValues.map((v) => ({ label: cleanLabels[v] || v, value: v }));
        const rule = findOptionRule(rules, { name: priceAxis, values: axisValues }, typeKey);
        const set = rule?.optionSetId && sets.some((s) => s.id === rule.optionSetId)
          ? await call<OptionSetRow>(`/api/option-sets/${rule.optionSetId}`, "PATCH", { etsyName: etsyName.trim() || undefined, values })
          : await call<OptionSetRow>("/api/option-sets", "POST", { name: priceAxis, kind: "list", etsyName: etsyName.trim() || undefined, values, defaultRole: "variation", productTypeKey: typeKey ?? undefined, inLibrary: false });
        // Tur ile hatirlanir (kolye/bileklik ayri kural)
        const next: OptionMapRule = { axisName: priceAxis, firstValue: axisValues[0] ?? "", optionSetId: set.id, role: "variation", productTypeKey: typeKey ?? null };
        const merged = [...rules.filter((r) => optionRuleKey(r) !== optionRuleKey(next)), next];
        await call("/api/settings", "PUT", { rexven_option_map: JSON.stringify(merged) });
      }
      // Setler
      await call(`${base}/option-refs`, "PATCH", { optionRefs: clearRefs ? null : refs });
      // Isim alani
      await call(`${base}/personalization`, "PATCH", { enabled: persoOn, slots, samples: samples.slice(0, slots), font, note });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["supplier-catalogs"] }),
        qc.invalidateQueries({ queryKey: ["supplier-catalog"] }),
        qc.invalidateQueries({ queryKey: ["shot-products"] }),
        qc.invalidateQueries({ queryKey: ["option-sets"] }),
        qc.invalidateQueries({ queryKey: ["settings", "rexven_option_map"] }),
      ]);
      setMsg("Kaydedildi");
      if (!startOpen) setOpen(false);
    } catch (e) { setMsg(e instanceof Error ? e.message : "Kaydedilemedi"); }
    finally { setBusy(false); }
  }

  const toggleSet = (s: OptionSetRow) => setRefs((rs) => rs.some((r) => r.optionSetId === s.id)
    ? rs.filter((r) => r.optionSetId !== s.id)
    : [...rs, { optionSetId: s.id, role: s.defaultRole === "personalization" || s.kind !== "list" ? "personalization" : "variation" }]);
  const patchRef = (id: string, p: Partial<ProductOptionRef>) => setRefs((rs) => rs.map((r) => (r.optionSetId === id ? { ...r, ...p } : r)));
  const toggleValue = (s: OptionSetRow, label: string) => {
    const ref = refs.find((r) => r.optionSetId === s.id);
    if (!ref) return;
    const all = s.values.map((v) => v.label);
    const cur = ref.values?.length ? ref.values : all;
    const next = cur.includes(label) ? cur.filter((x) => x !== label) : [...cur, label];
    patchRef(s.id, { values: next.length === all.length ? undefined : all.filter((x) => next.includes(x)) });
  };
  const shown = sets.filter((s) => refs.some((r) => r.optionSetId === s.id)
    || (s.inLibrary !== false && (!s.productTypeKey || !typeKey || s.productTypeKey === typeKey)));

  // ── Kapali: tek satir ozet (karttan detaya) ──
  if (!open) {
    const parts: string[] = [];
    if (storedPrice && priceReal) parts.push(`$ ${cat.axisTitles?.[storedPrice] || storedPrice}`);
    for (const r of stored ?? []) parts.push(`${nameOf(r.optionSetId)} (${r.role === "variation" ? "V" : "K"})`);
    if (p0?.enabled) parts.push(`isim ×${p0.slots}`);
    return (
      <button onClick={(e) => { e.stopPropagation(); if (onOpen) onOpen(); else { setRefs(stored ?? []); setOpen(true); } }}
        className="mb-2 block text-left text-[11px] text-fuchsia-700 hover:underline">
        {parts.length
          ? `☰ Seçenekler: ${parts.join(" · ")}`
          : `☰ Seçenekleri belirle${suggestion.refs.length ? ` · ${suggestion.refs.length} öneri` : ""}`}
      </button>
    );
  }

  const box = "rounded-xl border bg-white p-3";
  const h = "mb-2 flex items-center justify-between gap-2 text-sm font-semibold text-gray-800";

  return (
    <div className="space-y-3 text-sm" onClick={(e) => e.stopPropagation()}>
      {/* 1) Fiyat ekseni */}
      {multiAxes.length > 0 && (
        <div className={`${box} border-indigo-200`}>
          <div className={h}>
            <span>Fiyat ekseni <span className="text-xs font-normal text-gray-500">— fiyatı belirleyen eksen, Etsy'de 1. varyasyon</span></span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select value={priceAxis} onChange={(e) => { setPriceAxis(e.target.value); setLabels(cat.axisValueLabels?.[e.target.value] ?? {}); setEtsyName(cat.axisTitles?.[e.target.value] ?? ""); }}
              className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm">
              <option value="">Yok — tek fiyat</option>
              {multiAxes.map((a) => <option key={a.name} value={a.name}>{a.name} ({a.values.length})</option>)}
            </select>
            {priceAxis && (
              <>
                <input value={etsyName} onChange={(e) => setEtsyName(e.target.value)} placeholder="Etsy adı (ör. Number of Circles)"
                  className="min-w-[200px] flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm" />
                <button onClick={() => void aiLabels()} disabled={aiBusy}
                  className="flex items-center gap-1 rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50">
                  {aiBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI ile Etsy'ye uyarla
                </button>
              </>
            )}
          </div>
          {priceAxis && !priceReal && (
            <p className="mt-1.5 text-xs text-amber-700">Bu eksende fiyat değişmiyor — tek fiyatlı ürünse "Yok" seçip ekseni Seçenek setiyle listelemek daha doğru.</p>
          )}
          {priceAxis && (
            <>
              <div className="mt-2 grid max-h-64 grid-cols-1 gap-x-4 gap-y-1.5 overflow-y-auto sm:grid-cols-2">
                {axisValues.map((v) => (
                  <label key={v} className="flex items-center gap-2 text-xs">
                    <span className="w-40 shrink-0 truncate text-gray-600" title={v}>{v}</span>
                    <input value={labels[v] ?? ""} onChange={(e) => setLabels((l) => ({ ...l, [v]: e.target.value }))} placeholder={v}
                      className={`min-w-0 flex-1 rounded-md border px-2 py-1 text-sm ${(labels[v] ?? "").length > 20 ? "border-amber-400" : "border-gray-200"}`} />
                  </label>
                ))}
              </div>
              <label className="mt-2 flex items-center gap-1.5 text-xs text-gray-600">
                <input type="checkbox" checked={saveToLibrary} onChange={(e) => setSaveToLibrary(e.target.checked)} />
                Seçenek kütüphanesine kaydet ve hatırla (aynı ad + ilk değerli eksenler sonraki ürünlerde bu etiketlerle gelir)
              </label>
            </>
          )}
        </div>
      )}

      {/* 2) Secenek setleri */}
      <div className={`${box} border-fuchsia-200`}>
        <div className={h}>
          <span>Seçenek setleri <span className="text-xs font-normal text-gray-500">— V: varyasyon · K: kişiselleştirme</span></span>
          <button onClick={() => { setRefs(suggestion.refs); setReasons(suggestion.reasons); }} disabled={sets.length === 0}
            className="flex items-center gap-1 rounded-lg border border-fuchsia-300 bg-white px-2.5 py-1 text-xs font-medium text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-50">
            <Wand2 className="h-3.5 w-3.5" /> Otomatik algıla
          </button>
        </div>
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" /> : shown.length === 0 ? (
          <p className="text-xs text-gray-500">Seçenek Kütüphanesi boş — Şablonlar › Seçenek Kütüphanesi'nden set ekleyin.</p>
        ) : (
          <div className="space-y-1.5">
            {shown.map((s) => {
              const ref = refs.find((r) => r.optionSetId === s.id);
              return (
                <div key={s.id} className={`rounded-lg border px-2.5 py-1.5 ${ref ? (ref.role === "variation" ? "border-fuchsia-300 bg-fuchsia-50/40" : "border-orange-300 bg-orange-50/40") : "border-gray-200"}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="flex items-center gap-1.5 font-medium text-gray-800">
                      <input type="checkbox" checked={!!ref} onChange={() => toggleSet(s)} />
                      {s.name}{s.etsyName ? <span className="font-normal text-gray-400"> → {s.etsyName}</span> : null}
                    </label>
                    {s.chartImageUrl && <img src={abs(s.chartImageUrl)} alt="" className="h-6 w-6 rounded object-cover" title="Chart görseli" />}
                    {ref && (
                      <>
                        <select value={ref.role} onChange={(e) => patchRef(s.id, { role: e.target.value as ProductOptionRef["role"] })}
                          className="rounded-md border border-gray-200 px-1.5 py-0.5 text-xs">
                          {s.kind === "list" && <option value="variation">Varyasyon</option>}
                          <option value="personalization">Kişiselleştirme</option>
                        </select>
                        {ref.role === "personalization" && (
                          <label className="flex items-center gap-1 text-xs text-gray-600">
                            <input type="checkbox" checked={ref.required !== false} onChange={(e) => patchRef(s.id, { required: e.target.checked })} /> zorunlu
                          </label>
                        )}
                      </>
                    )}
                    {(reasons[s.id] || suggestion.reasons[s.id]) && ref && <span className="text-xs text-emerald-700">✓ {reasons[s.id] || suggestion.reasons[s.id]}</span>}
                  </div>
                  {ref && s.kind === "list" && s.values.length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {s.values.map((v) => {
                        const on = !ref.values?.length || ref.values.includes(v.label);
                        return (
                          <button key={v.label} onClick={() => toggleValue(s, v.label)}
                            className={`rounded-md border px-2 py-0.5 text-xs ${on ? "border-fuchsia-300 bg-white text-fuchsia-800" : "border-gray-200 text-gray-400 line-through"}`}>
                            {v.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {suggestion.unmatched.length > 0 && (
          <div className="mt-2 rounded-lg border border-dashed border-fuchsia-300 p-2">
            <p className="mb-1 text-xs text-gray-600">Kütüphanede karşılığı olmayan eksenler — set olarak kaydet ve ekle:</p>
            <div className="flex flex-wrap gap-1.5">
              {suggestion.unmatched.map((u) => (
                <button key={u.name} onClick={() => void createSetFor(u)} disabled={creatingSet !== null} title={u.values.join(", ")}
                  className="rounded-md border border-fuchsia-300 px-2 py-0.5 text-xs text-fuchsia-700 hover:bg-fuchsia-50 disabled:opacity-50">
                  {creatingSet === u.name ? "Oluşturuluyor…" : `＋ ${u.name} (${u.values.length}) · ${u.role === "variation" ? "V" : "K"}`}
                </button>
              ))}
            </div>
          </div>
        )}
        {suggestion.attributes.length > 0 && (
          <p className="mt-2 text-xs text-gray-500">
            Özellik olarak kalan: {suggestion.attributes.map((a) => `${a.name}: ${a.value}`).join(" · ")}
          </p>
        )}
      </div>

      {/* 3) Isim yazilan urun */}
      <div className={`${box} border-emerald-200`}>
        <label className={`${h} cursor-pointer justify-start`}>
          <input type="checkbox" checked={persoOn} onChange={(e) => setPersoOn(e.target.checked)} />
          <span>Ürüne isim/harf yazılıyor <span className="text-xs font-normal text-gray-500">— Studio fotoğrafa yazar, listeye isim alanı eklenir</span></span>
        </label>
        {persoOn && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="text-gray-600">İsim sayısı</span>
              <select value={slots} onChange={(e) => setSlots(Number(e.target.value))} className="rounded-md border border-gray-200 px-1.5 py-1 text-sm">
                {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
              {Array.from({ length: slots }).map((_, i) => (
                <input key={i} value={samples[i] ?? ""} placeholder={`Örnek ${i + 1}${i === 0 ? " (Emily)" : i === 1 ? " (Melissa)" : ""}`}
                  onChange={(e) => setSamples((prev) => { const n = [...prev]; n[i] = e.target.value; return n; })}
                  className="w-32 rounded-md border border-gray-200 px-2 py-1 text-sm" />
              ))}
            </div>
            <input value={font} onChange={(e) => setFont(e.target.value)} placeholder="Yazı tipi / stil (ör. ince el yazısı, script)"
              className="w-full rounded-md border border-gray-200 px-2 py-1 text-sm" />
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Not (ops.): yerleşim, büyük-küçük harf…"
              className="w-full rounded-md border border-gray-200 px-2 py-1 text-sm" />
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => void saveAll()} disabled={busy}
          className="rounded-lg bg-fuchsia-600 px-4 py-2 text-sm font-semibold text-white hover:bg-fuchsia-700 disabled:opacity-50">
          {busy ? "Kaydediliyor…" : "Seçenekleri kaydet"}
        </button>
        {stored !== undefined && (
          <button onClick={() => void saveAll(true)} disabled={busy} title="Ürüne özel setleri kaldır — şablonun seçenekleri kullanılır"
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs text-gray-600 hover:bg-gray-50">Setleri şablona bırak</button>
        )}
        {!startOpen && <button onClick={() => setOpen(false)} className="text-xs text-gray-500 hover:underline">Kapat</button>}
        {msg && <span className={msg === "Kaydedildi" ? "text-sm text-emerald-700" : "text-sm text-red-600"}>{msg}</span>}
      </div>
    </div>
  );
}
